import React, { useState, useMemo } from 'react';
import { User } from '../types/User';
import Sidebar from './Sidebar';
import ParticipantsList from './ParticipantsList';
import MyConnections from './MyConnections';
import MyCalendar from './MyCalendar';
import UserProfile from './UserProfile';
import MyProfile from './MyProfile';
import MeetingNotifications from './MeetingNotifications';
import ProgrammePage from './ProgrammePage';
import AdminPanel from './AdminPanel';
import ChatInterface from './ChatInterface';
import { MessageService } from '../services/messageService';
import { NetworkingService } from '../services/networkingService';
import { MeetingService } from '../services/meetingService';

interface DashboardProps {
  currentUser: User;
  onLogout: () => void;
  onUpdateUser: (user: User) => void;
}

type ActiveSection = 'networking' | 'profile' | 'programme' | 'about' | 'admin';
type ActiveTab = 'all' | 'connections' | 'calendar';

const Dashboard: React.FC<DashboardProps> = ({ currentUser, onLogout, onUpdateUser }) => {
  // Memoize currentUser to prevent unnecessary re-renders
  const memoizedCurrentUser = useMemo(() => currentUser, [currentUser.id]);
  
  const [activeSection, setActiveSection] = useState<ActiveSection>('networking');
  const [activeTab, setActiveTab] = useState<ActiveTab>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState<any[]>([]);

  const [connectionsData, setConnectionsData] = useState<any[]>([]);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [meetingsData, setMeetingsData] = useState<any[]>([]);
  const [meetingsLoaded, setMeetingsLoaded] = useState(false);

  // Load initial data with infinite scroll for participants
  React.useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load other data in parallel (participants will be loaded by ParticipantsList)
        const [unreadCount, connections, meetings] = await Promise.all([
          MessageService.getTotalUnreadCount(memoizedCurrentUser.id),
          NetworkingService.getUserConnections(memoizedCurrentUser.id),
          MeetingService.getUserMeetings(memoizedCurrentUser.id)
        ]);
        
        setUnreadCount(unreadCount);
        setConnectionsData(connections);
        setMeetingsData(meetings);
        setConnectionsLoaded(true);
        setMeetingsLoaded(true);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    
    loadInitialData();
    // Meeting and unread polling is paused while the project is under load.
    // Refresh still happens on navigation and after a meeting update.
  }, [memoizedCurrentUser.id]);


  const loadUnreadCount = async () => {
    const count = await MessageService.getTotalUnreadCount(memoizedCurrentUser.id);
    setUnreadCount(count);
  };

  const loadConversations = async () => {
    const convs = await MessageService.getUserConversations(memoizedCurrentUser.id);
    setConversations(convs);
  };

  const handleMeetingUpdate = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleNavigate = (section: string) => {
    setActiveSection(section as ActiveSection);
    setSelectedUser(null); // Close user profile panel when navigating
    
    // OPTIMIZATION: Only refresh unread count, participants data is loaded by useEffect
    if (section === 'networking') {
      loadUnreadCount();
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    // OPTIMIZATION: Only refresh unread count, conversations are managed by networking components
    MessageService.markMessagesAsRead(memoizedCurrentUser.id, user.id).then(() => {
      loadUnreadCount();
    });
  };

  const renderMainContent = () => {
    switch (activeSection) {
      case 'networking':
        return renderNetworkingContent();
      case 'profile':
        return <MyProfile currentUser={memoizedCurrentUser} onUpdateUser={onUpdateUser} />;
      case 'programme':
        return (
          <ProgrammePage />
        );
      case 'admin':
        return memoizedCurrentUser.role === 'admin' ? (
          <AdminPanel currentUser={memoizedCurrentUser} />
        ) : (
          <div className="p-6 text-center">
            <p className="text-gray-500">Access denied. Admin privileges required.</p>
          </div>
        );
      case 'about':
        return (
          <div className="p-4 lg:p-6 max-w-4xl mx-auto">
            {/* Hero Section */}
            <div className="text-white rounded-lg p-8 mb-8" style={{ backgroundColor: '#181C1D' }}>
              <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl lg:text-4xl font-bold mb-4">
                  <span className="text-white">LOGISTICS MARKET</span>{' '}
                  <span style={{ color: '#C89116' }}>Expo & Conference</span>
                </h1>
                <p className="text-sm lg:text-lg mb-6 leading-relaxed">
                  je vodeći logistički expo i forum u regionu, mesto gde se susreću stručnjaci, lideri i
                  inovatori iz sveta logistike. Ovaj jedinstveni događaj održaće se{' '}
                  <strong>6. oktobra 2026. godine</strong> na <strong>Beogradskom sajmu, Hala 5</strong>, sa{' '}
                  <strong>500+ učesnika</strong>.
                </p>
                
                <p className="text-sm lg:text-lg leading-relaxed opacity-90">
                  Događaj okuplja eminentne učesnike i omogućava uvid u najnovije tehnologije, proizvode i usluge,
                  pružajući dodatnu priliku za umrežavanje, razmenu ideja i uspostavljanje poslovnih kontakata.
                  Kroz niz predavanja, radionica i izložbenih štandova, učesnici će imati priliku da steknu uvid
                  u trendove i rešenja koja oblikuju budućnost logistike.
                </p>
              </div>
            </div>

            {/* Features Section */}
            <div className="space-y-8">
              {/* Feature 1 */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8 bg-white rounded-lg p-4 lg:p-8 shadow-sm border">
                <div className="flex justify-center mb-4 lg:mb-0 lg:flex-shrink-0">
                  <div className="w-20 h-20 flex items-center justify-center">
                    <img 
                      src="https://events.logisticsmarket.rs/wp-content/uploads/2025/01/Product-Brochure-EN.pdf-%E2%80%93-kopija-6.webp" 
                      alt="EXPO" 
                      className="w-full h-full object-cover rounded-lg"
                      style={{ transform: 'rotate(360deg)' }}
                    />
                  </div>
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                    Na Beogradskom sajmu, Hala 5, na štandovima izlagača biće predstavljena
                    najinteresantnija rešenja iz svih logističkih oblasti – intralogistike i transporta (drumskog, vodnog,
                    železničkog i vazdušnog).
                  </p>
                </div>
                <div className="w-full lg:w-1 h-1 lg:h-24 bg-gray-300 rounded-full mt-4 lg:mt-0"></div>
              </div>

              {/* Feature 2 */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8 bg-white rounded-lg p-4 lg:p-8 shadow-sm border">
                <div className="flex justify-center mb-4 lg:mb-0 lg:flex-shrink-0">
                  <div className="w-20 h-20 flex items-center justify-center">
                    <img 
                      src="https://events.logisticsmarket.rs/wp-content/uploads/2024/12/10.webp" 
                      alt="B2B Networking" 
                      className="w-full h-full object-cover rounded-lg"
                      style={{ transform: 'rotate(360deg)' }}
                    />
                  </div>
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                    Na B2B sastancima (13:30 – 15:30), srešće se zainteresovane strane za unapređenje
                    sopstvenog poslovanja (korisnici rešenja) i inovatori, provajderi i implementatori koji će svojim
                    rešenjima moći da odgovore na njihove zahteve (ponuđači rešenja), te već istog dana moći da
                    započnu ili finaliziraju dogovore o novim saradnjama.
                  </p>
                </div>
                <div className="w-full lg:w-1 h-1 lg:h-24 mt-4 lg:mt-0" style={{ backgroundColor: '#C89116' }}></div>
              </div>

              {/* Feature 3 */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8 bg-white rounded-lg p-4 lg:p-8 shadow-sm border">
                <div className="flex justify-center mb-4 lg:mb-0 lg:flex-shrink-0">
                  <div className="w-20 h-20 flex items-center justify-center">
                    <img 
                      src="https://events.logisticsmarket.rs/wp-content/uploads/2024/12/15.webp" 
                      alt="Predavanja" 
                      className="w-full h-full object-cover rounded-lg"
                      style={{ transform: 'rotate(360deg)' }}
                    />
                  </div>
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                    Na glavnoj i sporednoj bini biće održana odabrana predavanja i paneli eminentnih
                    predstavnika nauke i prakse. Tokom pauza za osveženje i networking, učesnici će imati
                    priliku za razmenu iskustava i uspostavljanje novih poslovnih kontakata.
                  </p>
                </div>
                <div className="w-full lg:w-1 h-1 lg:h-24 bg-gray-300 rounded-full mt-4 lg:mt-0"></div>
              </div>
            </div>
          </div>
        );
      default:
        return renderNetworkingContent();
    }
  };

  const renderNetworkingContent = () => {
    return (
      <div className="transition-all duration-300 ease-in-out">
        {activeTab === 'all' && (
          <div className="animate-fadeIn">
            <ParticipantsList 
              currentUser={memoizedCurrentUser}
              onSelectUser={handleUserSelect}
              meetingsData={meetingsData}
              meetingsLoaded={meetingsLoaded}
              onMeetingsUpdate={setMeetingsData}
            />
          </div>
        )}
        {activeTab === 'connections' && (
          <div className="animate-fadeIn">
            <MyConnections 
              currentUser={memoizedCurrentUser}
              onSelectUser={handleUserSelect}
              connectionsData={connectionsData}
              connectionsLoaded={connectionsLoaded}
              meetingsData={meetingsData}
              meetingsLoaded={meetingsLoaded}
              onConnectionsUpdate={setConnectionsData}
              onMeetingsUpdate={setMeetingsData}
            />
          </div>
        )}
        {activeTab === 'calendar' && (
          <div className="animate-fadeIn">
            <MyCalendar 
              currentUser={memoizedCurrentUser} 
              refreshTrigger={refreshTrigger}
              onMeetingUpdate={handleMeetingUpdate}
            />
          </div>
        )}
      </div>
    );
  };

  return (
      <div className="h-dvh max-h-dvh overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col lg:flex-row">
      {/* Meeting Notifications */}
      <MeetingNotifications currentUser={memoizedCurrentUser} onMeetingUpdate={handleMeetingUpdate} />
      
      {/* Sidebar */}
      <div className="flex-shrink-0">
        <Sidebar 
          currentUser={memoizedCurrentUser} 
          onLogout={onLogout} 
          onNavigate={handleNavigate}
          activeSection={activeSection}
        />
      </div>
      
      {/* Main Content */}
      <div className="flex-1 min-h-0 min-w-0 flex flex-col lg:flex-row">
        <div className="flex-1 min-h-0 min-w-0 flex flex-col">
          {/* Header - compact on phones, full on desktop */}
          <div className="bg-white/80 backdrop-blur-md shadow-lg border-b border-gray-200/50 header-stable h-14 lg:h-20">
            <div className="flex flex-row items-center justify-between px-3 lg:px-6 h-full w-full gap-3">
              {/* Left - Event Name */}
              <div className="flex-1 min-w-0 hidden lg:block">
                <h1 className="text-sm lg:text-lg font-bold truncate" style={{ color: '#000' }}>
                  LOGISTICS MARKET
                </h1>
                <p className="text-xs text-gray-500 font-medium hidden sm:block">Expo & Conference 2026</p>
              </div>
              
              {/* Center - Logo and Date on mobile */}
              <div className="flex items-center justify-center w-full lg:w-auto lg:flex-1 min-w-0">
                <img 
                  src="https://events.logisticsmarket.rs/wp-content/uploads/2022/02/Logistics-Market-logo-6.webp"
                  alt="Logistics Market"
                  className="h-6 lg:h-12 drop-shadow-sm transform hover:scale-105 transition-transform duration-200"
                />
                
                {/* Date on mobile - moved next to logo */}
                <div className="text-right lg:hidden ml-4">
                  <h1 className="text-sm font-bold" style={{ color: '#000' }}>
                    6 OCTOBER 2026
                  </h1>
                </div>
              </div>
              
              {/* Right - Event Date */}
              <div className="flex-1 flex justify-end min-w-0 hidden lg:block">
                <div className="text-right">
                  <h1 className="text-sm lg:text-lg font-bold truncate" style={{ color: '#000' }}>
                    6 OCTOBER 2026
                  </h1>
                  <p className="text-xs text-gray-500 font-medium hidden sm:block">Beogradski sajam, Hala 5</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation - Only show for networking section */}
          {activeSection === 'networking' && (
          <div className="bg-white border-b border-gray-200 flex-shrink-0">
            <div className="flex flex-row items-center px-2 lg:px-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('all')}
                className={`py-3 lg:py-4 px-3 lg:px-6 font-bold text-sm transition-all duration-300 border-b-2 whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'all'
                    ? 'border-yellow-500' 
                    : 'hover:text-gray-800 border-transparent'
                }`}
                style={{ color: activeTab === 'all' ? '#000' : '#444' }}
              >
                All Participants
              </button>
              <button
                onClick={() => setActiveTab('connections')}
                className={`py-3 lg:py-4 px-3 lg:px-6 font-bold text-sm transition-all duration-300 border-b-2 whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'connections'
                    ? 'border-yellow-500'
                    : 'hover:text-gray-800 border-transparent'
                }`}
                style={{ color: activeTab === 'connections' ? '#000' : '#444' }}
              >
                My Connections
              </button>
              <button
                onClick={() => setActiveTab('calendar')}
                className={`py-3 lg:py-4 px-3 lg:px-6 font-bold text-sm transition-all duration-300 border-b-2 whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'calendar'
                    ? 'border-yellow-500'
                    : 'hover:text-gray-800 border-transparent'
                }`}
                style={{ color: activeTab === 'calendar' ? '#000' : '#444' }}
              >
                My Calendar
              </button>
            </div>
          </div>
          )}

          {/* Content scrolls inside the screen, under the header and tabs */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="min-h-full transition-smooth">
              {renderMainContent()}
            </div>
          </div>
        </div>

        {/* Right Panel - User Profile */}
        {selectedUser && activeSection === 'networking' && (
          <ChatInterface 
            user={selectedUser} 
            currentUser={memoizedCurrentUser}
            onClose={() => {
              setSelectedUser(null);
              loadUnreadCount();
              loadConversations();
            }}
            onMessageSent={() => {
              loadUnreadCount();
              loadConversations();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;